/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { z } from 'zod';

/**
 * What one tab tells the others: somebody is here, or the session is over.
 *
 * The session is one cookie shared by every tab, but each tab used to keep its
 * own idle clock. A tab left open in the background timed out while the person
 * worked in another, and its sign-out ended the session everywhere -- the
 * tab they were typing in included. So activity is shared: a keystroke in any
 * tab resets the clock in all of them, and a tab times out only when nobody
 * has done anything in any of them.
 *
 * And when one tab does sign out, the rest are told at once, rather than
 * finding out on their next request with a page of data still on screen.
 *
 * Carried by a `BroadcastChannel` where the browser has one, and otherwise by
 * the `storage` event on a key holding nothing but a time. Neither carries a
 * token or anything about the session (`AUTH-5`): a moment somebody pressed a
 * key is all that crosses between tabs.
 */

/** The channel's name, and the storage keys of the fallback. */
export const ACTIVITY_CHANNEL = 'vellum-activity';
export const ACTIVITY_KEY = 'vellum-activity';
export const SIGNED_OUT_KEY = 'vellum-signed-out';

export const messageSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('active'), at: z.number() }),
  z.object({ kind: z.literal('signed-out') }),
]);

export type ActivityMessage = z.infer<typeof messageSchema>;

/** The part of `BroadcastChannel` used here, so a test can stand one in. */
export interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  close(): void;
}

/** The part of `window` the storage fallback needs. */
export interface StorageHost {
  localStorage: Pick<Storage, 'setItem'>;
  addEventListener(type: 'storage', listener: (event: StorageEvent) => void): void;
  removeEventListener(type: 'storage', listener: (event: StorageEvent) => void): void;
}

export interface ActivityEnv {
  /** Opens a channel, or is absent where the browser has none. */
  openChannel?: ((name: string) => ChannelLike) | undefined;
  /** The window, for the fallback. Absent outside a browser. */
  host?: StorageHost | undefined;
}

export interface ActivityBus {
  /** Somebody did something in this tab, at `at`. */
  announceActive(at: number): void;
  /** This tab has signed out. */
  announceSignedOut(): void;
  /** Hear what the other tabs say. Returns the way to stop. */
  listen(listener: (message: ActivityMessage) => void): () => void;
}

/** Read a storage event as a message, or nothing when it is not one of ours. */
function fromStorage(event: StorageEvent): ActivityMessage | null {
  if (event.newValue === null) return null;
  if (event.key === SIGNED_OUT_KEY) return { kind: 'signed-out' };
  if (event.key === ACTIVITY_KEY) {
    const at = Number(event.newValue);
    return Number.isFinite(at) ? { kind: 'active', at } : null;
  }
  return null;
}

/**
 * Build the bus over whatever the browser offers.
 *
 * The channel is opened when the first listener arrives and closed when the
 * last one leaves, so nothing stays open on a screen that is not listening.
 * Announcing with nobody listening in this tab opens a channel just long
 * enough to say it.
 */
export function createActivityBus(env: ActivityEnv): ActivityBus {
  const listeners = new Set<(message: ActivityMessage) => void>();
  let channel: ChannelLike | null = null;

  const deliver = (message: ActivityMessage) => {
    for (const listener of listeners) listener(message);
  };

  // Whatever arrives is another tab's say-so, and another tab could be
  // running older code: it is checked before it is believed.
  const onMessage = (event: MessageEvent) => {
    const parsed = messageSchema.safeParse(event.data);
    if (parsed.success) deliver(parsed.data);
  };

  const onStorage = (event: StorageEvent) => {
    const message = fromStorage(event);
    if (message) deliver(message);
  };

  const open = () => {
    if (env.openChannel) {
      channel = env.openChannel(ACTIVITY_CHANNEL);
      channel.addEventListener('message', onMessage);
    } else {
      env.host?.addEventListener('storage', onStorage);
    }
  };

  const close = () => {
    if (channel) {
      channel.removeEventListener('message', onMessage);
      channel.close();
      channel = null;
    } else {
      env.host?.removeEventListener('storage', onStorage);
    }
  };

  const post = (message: ActivityMessage) => {
    if (env.openChannel) {
      if (channel) {
        channel.postMessage(message);
        return;
      }
      const once = env.openChannel(ACTIVITY_CHANNEL);
      once.postMessage(message);
      once.close();
      return;
    }
    // The fallback writes a time and nothing else. A `storage` event fires in
    // every other tab only when the value changes, which a fresh time does.
    const key = message.kind === 'active' ? ACTIVITY_KEY : SIGNED_OUT_KEY;
    const value = message.kind === 'active' ? message.at : Date.now();
    try {
      env.host?.localStorage.setItem(key, String(value));
    } catch {
      // Storage refused -- a private window, blocked site data. The other
      // tabs keep their own clocks, which is how it worked before; nothing
      // here is worth interrupting anybody for.
    }
  };

  return {
    announceActive: (at) => {
      post({ kind: 'active', at });
    },
    announceSignedOut: () => {
      post({ kind: 'signed-out' });
    },
    listen: (listener) => {
      if (listeners.size === 0) open();
      listeners.add(listener);
      return () => {
        if (!listeners.delete(listener)) return;
        if (listeners.size === 0) close();
      };
    },
  };
}

/** What this browser offers, read once. */
function browserEnv(): ActivityEnv {
  const env: ActivityEnv = {};
  if (typeof BroadcastChannel === 'function') {
    env.openChannel = (name) => new BroadcastChannel(name);
  }
  if (typeof window !== 'undefined') {
    try {
      // Reading `localStorage` itself can throw where site data is blocked.
      env.host = {
        localStorage: window.localStorage,
        addEventListener: (type, listener) => {
          window.addEventListener(type, listener);
        },
        removeEventListener: (type, listener) => {
          window.removeEventListener(type, listener);
        },
      };
    } catch {
      // No storage, and no channel either, means each tab keeps its own
      // clock: the behaviour before any of this existed.
    }
  }
  return env;
}

/**
 * The tab's one bus.
 *
 * One, so a tab never hears itself: a `BroadcastChannel` does not deliver to
 * the instance that posted, and a `storage` event does not fire in the
 * document that wrote.
 */
export const activityBus: ActivityBus = createActivityBus(browserEnv());
